import { useCallback, useMemo } from "react";
import {
  EnumBookerStatus,
  EnumOrganizationMetadataStorefrontMode,
  EnumProductType,
  MembershipCheckoutBookersQuery,
  OrganizationByIdDocument,
  OrganizationsByUserDocument,
  useMembershipCheckoutBookersQuery,
  useOrganizationByIdQuery,
  useOrganizationUpdateMembershipPaymentsEnabledMutation,
  useOrganizationUpdateMembershipTierCheckoutMutation,
} from "gql/generated";
import { getMultilingualString } from "shared/utils";

export type MembershipCheckoutBooker =
  MembershipCheckoutBookersQuery["bookerMany"][number];

/** Exactly one of `bookerId` / `free` is set. */
export type TierCheckoutValue = { bookerId?: string; free?: boolean };

export type MembershipCheckoutOption = {
  value: string;
  label: string;
  booker: MembershipCheckoutBooker;
  isTest: boolean;
  isUsd: boolean;
};

const normId = (v: unknown): string | undefined =>
  v == null || v === "" ? undefined : String(v);

export const isMembershipCheckoutBooker = (booker: MembershipCheckoutBooker) =>
  booker.bundles?.[0]?.product?.type === EnumProductType.RenewableFee;

export const isBookerWiredForTier = (
  booker: MembershipCheckoutBooker,
  tierTagId: string,
) => {
  if ((booker.businessCompletionTags ?? []).map(String).includes(tierTagId)) {
    return true;
  }
  const options = booker.bundles?.[0]?.product?.sellingConfig?.options ?? [];
  return options.some(option =>
    (option?.tagConfig?.onCheckoutCompleted ?? [])
      .map(String)
      .includes(tierTagId),
  );
};

export const useMembershipPayment = (organizationId?: string) => {
  const { data: orgData, loading: orgLoading } = useOrganizationByIdQuery({
    variables: { id: organizationId ?? "" },
    skip: !organizationId,
  });
  const metadata = orgData?.organizationById?.metadata;

  const cadLive = normId(metadata?.connectedStorefront);
  const cadTest = normId(metadata?.connectedTestStorefront);
  const usdLive = normId(metadata?.connectedUsdStorefront);
  const usdTest = normId(metadata?.connectedUsdTestStorefront);
  const storefrontMode = metadata?.storefrontMode;

  const connectedIds = useMemo(
    () =>
      [cadLive, cadTest, usdLive, usdTest].filter(
        (id): id is string => !!id,
      ),
    [cadLive, cadTest, usdLive, usdTest],
  );
  const isStorefrontConnected = connectedIds.length > 0;

  const testIds = useMemo(
    () => new Set([cadTest, usdTest].filter(Boolean) as string[]),
    [cadTest, usdTest],
  );
  const usdIds = useMemo(
    () => new Set([usdLive, usdTest].filter(Boolean) as string[]),
    [usdLive, usdTest],
  );
  const showCurrency = !!(cadLive || cadTest) && !!(usdLive || usdTest);

  /** New membership widgets go to the test storefront when one exists. */
  const targetStorefrontId = useMemo(() => {
    const order =
      storefrontMode === EnumOrganizationMetadataStorefrontMode.Usd
        ? [usdTest, usdLive]
        : [cadTest, cadLive, usdTest, usdLive];
    return order.find(Boolean);
  }, [storefrontMode, cadTest, cadLive, usdTest, usdLive]);
  const targetIsTest = !!targetStorefrontId && testIds.has(targetStorefrontId);

  const { data: bookersData, loading: bookersLoading } =
    useMembershipCheckoutBookersQuery({
      variables: {
        filter: {
          OR: connectedIds.map(storefront => ({ storefront })),
          status: [
            EnumBookerStatus.Open,
            EnumBookerStatus.Closed,
            EnumBookerStatus.Draft,
          ],
        },
      },
      skip: connectedIds.length === 0,
    });

  const checkoutOptions: MembershipCheckoutOption[] = useMemo(
    () =>
      (bookersData?.bookerMany ?? [])
        .filter(isMembershipCheckoutBooker)
        .map(booker => {
          const name =
            getMultilingualString(booker.name ?? {}) ||
            getMultilingualString(booker.bundles?.[0]?.product?.name ?? {}) ||
            booker._id;
          return {
            value: String(booker._id),
            label: name,
            booker,
            isTest: testIds.has(String(booker.storefront)),
            isUsd: usdIds.has(String(booker.storefront)),
          };
        }),
    [bookersData, testIds, usdIds],
  );

  const tierCheckouts = useMemo(() => {
    const map = new Map<string, TierCheckoutValue>();
    for (const entry of metadata?.membershipTierCheckouts ?? []) {
      if (!entry?.tierTagId) continue;
      if (entry.bookerId) {
        map.set(String(entry.tierTagId), { bookerId: String(entry.bookerId) });
      } else if (entry.free) {
        map.set(String(entry.tierTagId), { free: true });
      }
    }
    return map;
  }, [metadata?.membershipTierCheckouts]);

  const paymentsEnabled =
    metadata?.membershipPaymentsEnabled ??
    (metadata?.membershipTierCheckouts ?? []).some(entry => !!entry?.bookerId);

  const [updatePaymentsEnabled, { loading: savingPaymentsEnabled }] =
    useOrganizationUpdateMembershipPaymentsEnabledMutation({
      refetchQueries: [OrganizationByIdDocument, OrganizationsByUserDocument],
      awaitRefetchQueries: true,
    });
  const setPaymentsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!organizationId) return;
      await updatePaymentsEnabled({ variables: { organizationId, enabled } });
    },
    [organizationId, updatePaymentsEnabled],
  );

  const renewalSettings = metadata?.renewalSettings;
  const canCreateMembershipProduct =
    !!renewalSettings?.enabled &&
    renewalSettings.term !== "MANUAL" &&
    renewalSettings.term !== "LIFETIME";

  const [updateTierCheckout, { loading: saving }] =
    useOrganizationUpdateMembershipTierCheckoutMutation({
      refetchQueries: [OrganizationByIdDocument, OrganizationsByUserDocument],
      awaitRefetchQueries: true,
    });

  const saveTierCheckout = useCallback(
    async (tierTagId: string, value: TierCheckoutValue | null) => {
      if (!organizationId) return;
      await updateTierCheckout({
        variables: {
          organizationId,
          tierTagId,
          bookerId: value?.bookerId ?? null,
          free: value?.free ?? null,
        },
      });
    },
    [organizationId, updateTierCheckout],
  );

  return {
    loading: orgLoading || bookersLoading,
    paymentsEnabled,
    setPaymentsEnabled,
    savingPaymentsEnabled,
    isStorefrontConnected,
    targetStorefrontId,
    targetIsTest,
    showCurrency,
    checkoutOptions,
    tierCheckouts,
    renewalSettings,
    connectedOperatorId: normId(metadata?.connectedOperator),
    canCreateMembershipProduct,
    saveTierCheckout,
    saving,
  };
};
